import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getCourse, myEnrollments, completeLesson, attemptQuiz, certificateUrl } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { ArrowLeft, ArrowRight, Check, Award, Loader2, PlayCircle } from "lucide-react";

const CourseLearn = () => {
  const { slug } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [activeIdx, setActiveIdx] = useState({ m: 0, l: 0 });
  const [quizModule, setQuizModule] = useState(null); // module id for quiz view
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) { nav(`/auth/login?redirect=/academy/${slug}/learn`); return; }
    getCourse(slug).then(setCourse);
    myEnrollments().then((list) => {
      const e = list.find((x) => x.course_slug === slug);
      setEnrollment(e || null);
    });
  }, [slug, user, loading, nav]);

  const allLessons = useMemo(() => {
    if (!course) return [];
    const flat = [];
    course.modules.forEach((m, mi) => m.lessons.forEach((l, li) => flat.push({ ...l, _module: m, mi, li })));
    return flat;
  }, [course]);

  const activeLesson = course?.modules?.[activeIdx.m]?.lessons?.[activeIdx.l];
  const activeModule = course?.modules?.[activeIdx.m];

  const completedSet = new Set(enrollment?.completed_lesson_ids || []);
  const quizResults = enrollment?.module_quiz_results || {};

  const isModulePassed = (m) => !m.quiz?.length || quizResults[m.id]?.passed;

  const markComplete = async () => {
    if (!activeLesson) return;
    setBusy(true); setErr("");
    try {
      const r = await completeLesson(slug, activeLesson.id);
      setEnrollment(r);
    } catch (e) { setErr(e?.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  const submitQuiz = async () => {
    setBusy(true); setErr("");
    try {
      const r = await attemptQuiz(slug, quizModule.id, quizAnswers);
      setQuizResult(r);
      // refresh enrollment
      const list = await myEnrollments();
      setEnrollment(list.find((x) => x.course_slug === slug) || null);
    } catch (e) { setErr(e?.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  if (loading || !course) return <div className="pt-32 text-center text-white/55">Lade…</div>;
  if (!enrollment) return (
    <div className="pt-32 text-center" data-testid="learn-not-enrolled">
      <p className="text-white/75 mb-4">Du bist nicht eingeschrieben.</p>
      <Link to={`/academy/${slug}`} className="btn-primary">Zur Kursseite</Link>
    </div>
  );

  const courseComplete = enrollment?.completed;

  return (
    <div className="pt-24 min-h-screen" data-testid={`learn-${slug}`}>
      <div className="container-sat py-8">
        <Link to="/academy/dashboard" className="btn-ghost text-sm"><ArrowLeft size={14} /> Mein Dashboard</Link>

        <div className="grid lg:grid-cols-12 gap-8 mt-6">
          {/* Sidebar */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start space-y-3" data-testid="learn-sidebar">
            <h2 className="serif text-2xl mb-3">{course.title}</h2>
            {course.modules.map((m, mi) => (
              <div key={m.id} className="glass rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="overline">Modul {mi + 1}</div>
                  {isModulePassed(m) ? <Check size={14} className="text-[#D4AF37]" /> : null}
                </div>
                <div className="serif text-lg mb-2">{m.title}</div>
                <ul className="space-y-1.5">
                  {m.lessons.map((l, li) => {
                    const isActive = !quizModule && activeIdx.m === mi && activeIdx.l === li;
                    const done = completedSet.has(l.id);
                    return (
                      <li key={l.id}>
                        <button
                          onClick={() => { setQuizModule(null); setQuizResult(null); setActiveIdx({ m: mi, l: li }); }}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition ${isActive ? "bg-white/[0.07] text-white" : "text-white/65 hover:bg-white/[0.03]"}`}
                          data-testid={`lesson-link-${mi}-${li}`}
                        >
                          {done ? <Check size={14} className="text-[#D4AF37]" /> : <PlayCircle size={14} className="text-white/30" />}
                          <span className="flex-1">{l.title}</span>
                        </button>
                      </li>
                    );
                  })}
                  {m.quiz?.length > 0 && (
                    <li>
                      <button
                        onClick={() => { setQuizModule(m); setQuizAnswers({}); setQuizResult(null); }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition ${quizModule?.id === m.id ? "bg-[#D4AF37] text-[#081328]" : "text-[#D4AF37] hover:bg-[#D4AF37]/10"}`}
                        data-testid={`quiz-link-${mi}`}
                      >
                        <Award size={14} /> Modul-Quiz {quizResults[m.id]?.passed ? `(${quizResults[m.id].score}/100 ✓)` : ""}
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            ))}
            {courseComplete && (
              <a href={certificateUrl(slug)} target="_blank" rel="noreferrer" className="btn-primary w-full justify-center" data-testid="certificate-btn">
                <Award size={16} /> Zertifikat herunterladen
              </a>
            )}
          </aside>

          {/* Main */}
          <main className="lg:col-span-8">
            {!quizModule && activeLesson && (
              <div className="glass rounded-2xl p-6 md:p-10" data-testid="lesson-view">
                <div className="overline mb-2">{activeModule.title}</div>
                <h1 className="serif text-3xl md:text-5xl font-light mb-6">{activeLesson.title}</h1>

                {activeLesson.video_url && (
                  <div className="aspect-video rounded-xl overflow-hidden border border-white/10 mb-6">
                    <iframe src={activeLesson.video_url} title={activeLesson.title} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen></iframe>
                  </div>
                )}

                <div className="prose prose-invert max-w-none text-white/80 leading-relaxed text-lg">
                  {activeLesson.body.split("\n").map((p, i) => <p key={i}>{p}</p>)}
                </div>

                <div className="mt-8 flex flex-wrap gap-3 justify-between">
                  <button
                    onClick={() => {
                      // prev
                      if (activeIdx.l > 0) setActiveIdx({ ...activeIdx, l: activeIdx.l - 1 });
                      else if (activeIdx.m > 0) {
                        const pm = course.modules[activeIdx.m - 1];
                        setActiveIdx({ m: activeIdx.m - 1, l: pm.lessons.length - 1 });
                      }
                    }}
                    className="btn-ghost text-sm"
                  >
                    <ArrowLeft size={14} /> Zurück
                  </button>
                  <div className="flex gap-3">
                    {!completedSet.has(activeLesson.id) ? (
                      <button onClick={markComplete} disabled={busy} className="btn-secondary text-sm" data-testid="lesson-complete">
                        {busy ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Als erledigt markieren
                      </button>
                    ) : (
                      <span className="btn-secondary text-sm cursor-default"><Check size={14} className="text-[#D4AF37]" /> Erledigt</span>
                    )}
                    <button
                      onClick={() => {
                        const m = course.modules[activeIdx.m];
                        if (activeIdx.l < m.lessons.length - 1) setActiveIdx({ ...activeIdx, l: activeIdx.l + 1 });
                        else if (activeIdx.m < course.modules.length - 1) setActiveIdx({ m: activeIdx.m + 1, l: 0 });
                      }}
                      className="btn-primary text-sm"
                      data-testid="lesson-next"
                    >
                      Weiter <ArrowRight size={14} />
                    </button>
                  </div>
                </div>
                {err && <p className="text-red-300 text-xs mt-3">{err}</p>}
              </div>
            )}

            {quizModule && (
              <div className="glass rounded-2xl p-6 md:p-10" data-testid="quiz-view">
                <div className="overline mb-2">Modul-Quiz</div>
                <h1 className="serif text-3xl md:text-5xl font-light mb-6">{quizModule.title}</h1>

                {!quizResult ? (
                  <div className="space-y-6">
                    {quizModule.quiz.map((q, qi) => (
                      <div key={q.id} className="border-t border-white/5 pt-5" data-testid={`quiz-q-${qi}`}>
                        <div className="serif text-xl mb-4"><span className="num-mark mr-2">{qi + 1}</span>{q.q}</div>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {q.options.map((opt, oi) => (
                            <button
                              key={oi}
                              onClick={() => setQuizAnswers({ ...quizAnswers, [q.id]: oi })}
                              className={`text-left px-4 py-3 rounded-lg border transition ${quizAnswers[q.id] === oi ? "bg-[#D4AF37] text-[#081328] border-[#D4AF37]" : "border-white/10 text-white/75 hover:bg-white/[0.04]"}`}
                              data-testid={`quiz-q-${qi}-opt-${oi}`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    <div className="pt-4">
                      <button onClick={submitQuiz} disabled={busy || Object.keys(quizAnswers).length < quizModule.quiz.length} className="btn-primary" data-testid="quiz-submit-btn">
                        {busy ? <Loader2 className="animate-spin" size={16} /> : <>Antworten prüfen <ArrowRight size={16} /></>}
                      </button>
                      {err && <p className="text-red-300 text-xs mt-2">{err}</p>}
                    </div>
                  </div>
                ) : (
                  <div data-testid="quiz-result-view">
                    <div className={`serif text-5xl ${quizResult.passed ? "text-[#D4AF37]" : "text-white/85"}`}>{quizResult.score} / 100</div>
                    <p className="text-white/65 mt-3">{quizResult.passed ? "Bestanden — du darfst weiterziehen." : "Noch nicht bestanden — schau dir das Modul nochmal an und versuche es erneut."}</p>
                    <div className="mt-6 flex gap-3">
                      <button onClick={() => { setQuizResult(null); setQuizAnswers({}); }} className="btn-secondary text-sm">Erneut versuchen</button>
                      {quizResult.passed && (
                        <button onClick={() => { setQuizModule(null); setQuizResult(null); }} className="btn-primary text-sm">Weiter zum nächsten Modul <ArrowRight size={14} /></button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default CourseLearn;
